import { NavLink } from 'react-router';
import type { ConversationRow } from '@st/contract';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDay, formatTime } from '../../lib/format';
import { cx } from '../../lib/cx';
import { Avatar } from '../../ui';

export interface ConversationListProps {
  items: readonly ConversationRow[];
  basePath: string;
  activeId: string | null;
}

const isToday = (iso: string): boolean => iso.slice(0, 10) === new Date().toISOString().slice(0, 10);

/** Redak pregleda: tko je zadnji pisao i što. Privitak bez teksta ima svoj natpis. */
function pregled(c: ConversationRow, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const zadnja = c.lastMessage;
  if (!zadnja) return t('chat.lastMessageNone');
  const tekst = zadnja.body?.trim()
    ? zadnja.body.trim()
    : zadnja.hasAttachment
      ? t('chat.attachmentPreview')
      : t('chat.lastMessageNone');
  return zadnja.mine ? t('chat.youPrefix', { text: tekst }) : tekst;
}

export function ConversationList({ items, basePath, activeId }: ConversationListProps) {
  const { t, locale } = useTranslation();

  return (
    <ul className="flex flex-col gap-1" data-testid="conversation-list">
      {items.map((c) => (
        <li key={c.id}>
          <NavLink
            to={`${basePath}/${c.id}`}
            className={cx(
              'flex items-center gap-3 rounded-md p-3 transition-colors duration-200',
              c.id === activeId ? 'bg-accent-light' : 'hover:bg-bg-alt',
            )}
          >
            <Avatar name={c.partnerName} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[14.5px] text-ink">{c.partnerName}</span>
                <span className="shrink-0 text-[11px] text-ink-muted">
                  {c.lastMessageAt
                    ? isToday(c.lastMessageAt)
                      ? formatTime(c.lastMessageAt, locale)
                      : formatDay(c.lastMessageAt, locale)
                    : ''}
                </span>
              </span>
              {/*
                Pregled zadnje poruke. Bez njega je popis bio niz imena i
                vremena — trener nije mogao odlučiti kome se javiti prvo a da ne
                otvori svaki razgovor. „Ti:" ispred vlastite poruke razlikuje
                „čeka moj odgovor" od „ja sam zadnji pisao".
              */}
              <span className="block truncate text-[12px] text-ink-muted">
                {pregled(c, t)}
              </span>
            </span>
            {c.unread > 0 && (
              <span
                aria-label={t('chat.unread', { n: c.unread })}
                className="shrink-0 rounded-pill bg-accent-dark px-2 py-0.5 text-[11px] text-surface tabular-nums"
              >
                {c.unread}
              </span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
